package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBochkaPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("bochka.md", FrontMatterElement.Builder { addScalar("title", "bochka"); addScalar("searchTitle", "S3-compatible object store on the JVM — one process, one disk, starts in a test"); addScalar("description", "An S3 store you can run on one machine or start inside a test. 518 of 744 ceph/s3-tests, with a classified reason on every remaining failure."); addScalar("library", "bochka"); addScalar("post", "/blog/silence-is-not-a-clean-run"); }))
}

@Page("/bochka")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun BochkaPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two gaps, and the same server fills both.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the JVM, \"an S3 endpoint you can start in a test\" is a mock, and a mock answers what it was told to answer — it will accept a signature it never verified and a body framed in a way no real client sends. The alternatives are a container of MinIO, which is a distributed store running in a mode it was not built for, or the real thing over the network, which is not a test.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The other gap is the small deployment: one machine, one disk, objects that have to survive a kill. Everything in that space is either a cluster with a coordinator or a toy that loses data on a power cut.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("All four ways a client can frame a body")
            }
            org.jetbrains.compose.web.dom.Text(" — signed payload, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("UNSIGNED-PAYLOAD")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("STREAMING-AWS4-HMAC-SHA256-PAYLOAD")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("STREAMING-UNSIGNED-PAYLOAD-TRAILER")
            }
            org.jetbrains.compose.web.dom.Text(" — because the default for ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("aws s3 cp")
            }
            org.jetbrains.compose.web.dom.Text(" is the third one, and a server that reads ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Content-Length")
            }
            org.jetbrains.compose.web.dom.Text(" bytes off a socket does not work with it at all.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("SigV4 verified against all 34 official AWS vectors")
            }
            org.jetbrains.compose.web.dom.Text(", run in the verifying direction, plus presigned URLs and browser ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("POST")
            }
            org.jetbrains.compose.web.dom.Text(" form uploads with both signature versions.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Objects, properly")
            }
            org.jetbrains.compose.web.dom.Text(": ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Range")
            }
            org.jetbrains.compose.web.dom.Text(", metadata, checksums in both forms, server-side copy, conditional reads and writes, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("GetObjectAttributes")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("partNumber")
            }
            org.jetbrains.compose.web.dom.Text(" on a read, multipart upload with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("UploadPartCopy")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Versioning, object lock and lifecycle rules")
            }
            org.jetbrains.compose.web.dom.Text(" — and lifecycle rules that are ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("applied")
            }
            org.jetbrains.compose.web.dom.Text(": objects and noncurrent versions expire, orphaned delete markers and abandoned uploads go, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("x-amz-expiration")
            }
            org.jetbrains.compose.web.dom.Text(" says when.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Durability that was tested by killing it.")
            }
            org.jetbrains.compose.web.dom.Text(" The write order's worst outcome is an orphaned file, never a key pointing at nothing; the index is a log with a checksum per record; crash tests ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIGKILL")
            }
            org.jetbrains.compose.web.dom.Text(" a child JVM mid-write and require everything the log admitted to to read back.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("GET")
                }
                org.jetbrains.compose.web.dom.Text(" is ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("transferTo")
                }
                org.jetbrains.compose.web.dom.Text(" from the file straight into the socket")
            }
            org.jetbrains.compose.web.dom.Text(" — measured at 7.6–8.0× less processor per byte than reading into the heap, across a real network card.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A published ceiling rather than a surprise.")
            }
            org.jetbrains.compose.web.dom.Text(" Every key lives in memory, so the number of versions is bounded by the heap: 399 215 on the shipped 512 MiB profile, printed on the first line of the log. Going over it is a refusal to start, not a slide into swap.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-is-not") }) {
        org.jetbrains.compose.web.dom.Text("What it is not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One process, one node, no replication. No ACLs, bucket policies or IAM — the access keys are a static list, narrowed to a mode and a set of buckets. No TLS inside the process: terminating it here would wrap the socket and silently remove the zero-copy read path. No storage classes, so a lifecycle rule carrying a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Transition")
        }
        org.jetbrains.compose.web.dom.Text(" is refused by name rather than stored and never acted on.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-number") }) {
        org.jetbrains.compose.web.dom.Text("The number")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("518 of 744")
        }
        org.jetbrains.compose.web.dom.Text(" as it ships and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("533 of 744")
        }
        org.jetbrains.compose.web.dom.Text(" with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("BOCHKA_ANONYMOUS=1")
        }
        org.jetbrains.compose.web.dom.Text(", in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ceph/s3-tests")
        }
        org.jetbrains.compose.web.dom.Text(", revision ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("5522d1c")
        }
        org.jetbrains.compose.web.dom.Text(". Both are measured rather than derived: the switch was predicted to give 529 and the run gave 532, and the difference was a defect the run itself found.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The remaining 226 carry a classified reason each — 213 deliberate scope decisions, eleven that pass only behind that switch, two waiting on a source rather than on work, zero known defects — and a failure matching no rule is reported by name as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unclassified")
        }
        org.jetbrains.compose.web.dom.Text(" rather than folded into a category. That count is zero, and the reasons behind it are re-read when a feature lands, because a rule keeps matching long after its reason stops being true.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("run-it") }) {
        org.jetbrains.compose.web.dom.Text("Run it")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""docker run -d --name bochka \
  -u 1000:1000 \
  -v /srv/bochka:/var/lib/bochka \
  -p 127.0.0.1:9000:9000 \
  -e BOCHKA_KEYS='youraccesskey:yoursecretkey' \
  ghcr.io/youndie/bochka:latest
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("start-it-inside-a-test") }) {
        org.jetbrains.compose.web.dom.Text("Start it inside a test")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""repositories { maven("https://reposilite.kotlin.website/snapshots") }
dependencies { testImplementation("io.github.youndie.bochka:bochka-embedded:0.4.0") }
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""class ReportsTest {
    companion object {
        @JvmField
        @RegisterExtension
        val bochka = BochkaExtension()
    }

    @Test
    fun `uploads the report`() {
        // bochka.endpoint, bochka.accessKeyId, bochka.secretKey — point any SDK at it
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same server as the image: same signature verification, same body framings, same storage. It also does the one thing a real store cannot and a test double should — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("failNext(503)")
        }
        org.jetbrains.compose.web.dom.Text(" makes the next request fail, so client code whose retries were never exercised gets exercised.")
    }
}
